import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import Modal from "../Modal";
import Button from "../Button";
import RadioButton from "../RadioButton";
import Tooltip from "../Tooltip";
import { CircleCheckSolidV6, TriangleExclamationSolidV6 } from "../Icomoon";
import Loading from "../../../Goals/components/Loader";
import { isMailableAddress } from "../utils";

const formatAddress = (address) => {
  const parts = [address.street_address];
  if (address.secondary_address) {
    parts.push(address.secondary_address);
  }
  const cityStateZip = [address.city, address.locality, address.postal_code].filter(Boolean).join(", ");
  if (cityStateZip) {
    parts.push(cityStateZip);
  }
  return parts.join(", ");
};

const formatAddressType = (type) => {
  if (!type) return "";
  return type.charAt(0).toUpperCase() + type.slice(1);
};

const AddressPickerModal = ({
  show,
  onHide,
  addresses,
  onSelect,
  isAddressValid = isMailableAddress,
  getInvalidReason = null,
  isLoading = false,
}) => {
  const validAddresses = useMemo(() => addresses.filter(isAddressValid), [addresses, isAddressValid]);

  const [selectedAddressId, setSelectedAddressId] = useState(() => validAddresses[0]?.id || null);

  const handleNext = () => {
    const selectedAddress = addresses.find((addr) => addr.id === selectedAddressId);
    if (selectedAddress) {
      onSelect(selectedAddress);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      contentSize="medium"
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
    >
      <Modal.Header title="Select Address" closeButton />
      <Modal.Body className="tw-py-[24px] tw-px-0">
        {isLoading ? (
          <Loading />
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-[16px]">
            {addresses.map((address) => {
              const isValid = isAddressValid(address);
              const isStandardized = address.standardized === "standardized";
              const addressLabel = `${formatAddress(address)} (${formatAddressType(address.address_type)})`;
              const invalidReason = !isValid && getInvalidReason ? getInvalidReason(address) : null;

              const warningTooltip = invalidReason || "Address not verified";

              return (
                <div key={address.id} className="tw-flex tw-items-center tw-gap-[8px]">
                  <RadioButton
                    name="address-picker"
                    value={address.id}
                    label={addressLabel}
                    checked={selectedAddressId === address.id}
                    disabled={!isValid}
                    onChange={() => setSelectedAddressId(address.id)}
                    labelClassName={!isValid ? "tw-text-neutral-gray-30" : ""}
                  />
                  {isValid && isStandardized ? (
                    <Tooltip
                      placement="top"
                      trigger={<CircleCheckSolidV6 size="m" className="tw-text-brand-info" />}
                      content="Address verified"
                    />
                  ) : (
                    <Tooltip
                      placement="top"
                      trigger={<TriangleExclamationSolidV6 size="m" className="tw-text-[#E5941B]" />}
                      content={warningTooltip}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="tw-flex tw-justify-between tw-gap-[8px]">
        <Button schema="tertiary" size="medium" onClick={onHide}>
          Cancel
        </Button>
        <Button schema="primary" size="medium" onClick={handleNext} disabled={!selectedAddressId}>
          Next
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

AddressPickerModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  addresses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      street_address: PropTypes.string,
      secondary_address: PropTypes.string,
      city: PropTypes.string,
      locality: PropTypes.string,
      postal_code: PropTypes.string,
      address_type: PropTypes.string,
      standardized: PropTypes.string,
    }),
  ).isRequired,
  onSelect: PropTypes.func.isRequired,
  isAddressValid: PropTypes.func,
  getInvalidReason: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default AddressPickerModal;
